import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError, resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, Loader2, Users } from "lucide-react";

export default function InvitePage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null); // null=loading, false=invalid, obj=ok
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/invites/${token}`)
      .then((res) => setInvite(res.data))
      .catch(() => setInvite(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await api.post(`/invites/${token}/accept`, { email, password });
      localStorage.setItem("st_token", res.data.token);
      window.location.href = "/";
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not accept invite");
      setSubmitting(false);
    }
  };

  if (invite === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="w-10 h-10 border-2 border-[#15803D] border-t-transparent rounded-full animate-spin" />
      </div>
    );

  if (invite === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] p-8 text-center">
        <img src="/crest.png" alt="5N2 Footy Academy crest" className="w-16 h-16 object-contain mb-4" />
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">Invite Unavailable</h1>
        <p className="text-[#A1A1AA] mt-2 max-w-sm">This invite link is invalid, has already been used, or has expired. Ask your coach for a new one.</p>
      </div>
    );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[#141414] border border-[#27272A]"
      >
        <div className="relative h-40 bg-[#1A1A1A] overflow-hidden">
          {invite.photo_url ? (
            <img src={resolveImg(invite.photo_url)} alt={invite.player_name} className="w-full h-full object-cover object-top" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#3f3f46]"><Users className="w-12 h-12" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#141414] to-transparent" />
          <span className="absolute top-2 right-4 font-display text-6xl font-black text-white/15">{invite.jersey_number}</span>
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <img src="/crest.png" alt="5N2 Footy Academy crest" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg font-black uppercase tracking-tight">5N2 Academy</span>
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#71717A]">Claim your profile</p>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight mt-1">{invite.player_name}</h1>
          <p className="text-sm text-[#A1A1AA] mt-1 mb-6">{invite.position} · #{invite.jersey_number} — set a password to manage your profile.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">Email</Label>
              <Input data-testid="invite-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0A0A0A] border-[#27272A] rounded-none h-12 focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]" placeholder="you@email.com" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A1A1AA]">Create Password</Label>
              <Input data-testid="invite-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0A0A0A] border-[#27272A] rounded-none h-12 focus:border-[#15803D] focus:ring-1 focus:ring-[#15803D]" placeholder="At least 6 characters" />
            </div>
            {error && <p data-testid="invite-error" className="text-sm text-red-500 font-medium">{error}</p>}
            <Button type="submit" data-testid="invite-submit-button" disabled={submitting} className="w-full h-12 rounded-none bg-[#15803D] hover:bg-[#166534] text-white font-bold uppercase tracking-[0.15em] transition-colors">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Claim My Profile"}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
